import { useState } from "react";
import axios from "axios";
import { Loader2, Check, Handshake } from "lucide-react";
import { SectionHead, inputCls, labelCls } from "@/components/site/SectionHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Attorney / Law Firm", "Public Adjuster", "Engineer / Expert", "Contractor / Restoration", "Insurance Professional", "Property Manager", "Other"];
const EMPTY = { name: "", email: "", phone: "", company: "", partner_category: CATEGORIES[0], message: "" };

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export const PartnerApply = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return setError("Please complete the required fields.");
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/consultations`, { ...form, type: "partner_application" });
      setStatus("success");
      setForm(EMPTY);
    } catch (_) {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="partner-apply" className="relative py-20 sm:py-24 md:py-32 btr-bg-graphite overflow-hidden" data-testid="partner-apply-section">
      <div className="absolute inset-0 btr-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-start">
        <SectionHead
          badge="Trusted Partner Network"
          title={<>Join the professionals who work <span className="text-[#a2020b]">before</span> the risk.</>}
          lede="We collaborate with attorneys, adjusters, engineers, contractors, and insurance professionals who share our standard for documentation and integrity. Applications are reviewed confidentially."
        />
        <form onSubmit={submit} className="btr-reveal btr-glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid="partner-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *"><input name="name" value={form.name} onChange={set} className={inputCls} placeholder="Jane Doe" data-testid="partner-name" /></Field>
            <Field label="Email *"><input name="email" type="email" value={form.email} onChange={set} className={inputCls} placeholder="jane@firm.com" data-testid="partner-email" /></Field>
            <Field label="Phone"><input name="phone" value={form.phone} onChange={set} className={inputCls} placeholder="(555) 000-0000" data-testid="partner-phone" /></Field>
            <Field label="Company / Firm"><input name="company" value={form.company} onChange={set} className={inputCls} placeholder="Firm name" data-testid="partner-company" /></Field>
          </div>
          <Field label="Profession *">
            <select name="partner_category" value={form.partner_category} onChange={set} className={inputCls} data-testid="partner-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Expertise & Service Area *">
            <textarea name="message" rows={4} value={form.message} onChange={set} className={`${inputCls} resize-none`} placeholder="Licenses, specialties, and regions you serve." data-testid="partner-message" />
          </Field>
          {error && <div className="text-[#ff6b72] text-xs font-body" data-testid="partner-error">{error}</div>}
          {status === "success" && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-body" data-testid="partner-success">
              <Check className="w-4 h-4" /> Application received. Our team will review it and be in touch.
            </div>
          )}
          <button type="submit" disabled={status === "loading"} className="btr-btn-primary w-full sm:w-auto" data-testid="partner-submit">
            {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />} Submit Application
          </button>
        </form>
      </div>
    </section>
  );
};
